import * as React from "react";
import WordIcon from "../../../../assets/Word.svg";
import { createLogger } from "../../../utils/logger";
import { getErrorMessage } from "../../../types/errors";
import { track } from "../../../utils/analytics";
import { downloadBlob } from "../../../utils/downloadBlob";
import { getDocumentName } from "../../../helpers/word";
import { certificateBody, certificateFilename } from "./certificateRoute";

const log = createLogger("ExportToWordButton");

/**
 * Widths of the certificate's three columns. The middle one carries the
 * clause-by-clause basis and needs the room; the other two hold a few words.
 */
const CERTIFICATE_COLUMN_WIDTHS = [28, 47, 25];

/** How long the failure label stays before the button offers another try. */
const FAILURE_LABEL_MS = 2500;

interface ExportToWordButtonProps {
  /** The answer's markdown, without its Prompt Coach section. */
  markdown: string;
}

type ExportState = "idle" | "exporting" | "failed";

/**
 * Downloads a certificate answer as a Word document.
 *
 * Offered only on the answer to a Draft Certificate request. The document
 * builder is loaded on first use rather than with the pane: it is a sizeable
 * dependency serving one button that most sessions never press.
 */
const ExportToWordButton: React.FC<ExportToWordButtonProps> = ({ markdown }) => {
  const [state, setState] = React.useState<ExportState>("idle");
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => {
    if (state !== "failed") return;
    const timer = window.setTimeout(() => setState("idle"), FAILURE_LABEL_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const handleExport = async () => {
    if (state === "exporting") return;
    setState("exporting");
    const body = certificateBody(markdown);
    try {
      const { buildDocxBlob } = await import("../../../utils/markdownToDocx");
      const blob = await buildDocxBlob(body, {
        title: "Tier 3 Draft Certificate",
        columnWidthsPercent: CERTIFICATE_COLUMN_WIDTHS,
      });
      downloadBlob(blob, certificateFilename(getDocumentName()));
      setState("idle");
      track("certificate:exported", { success: true, header_found: body !== markdown });
      log.info("Certificate exported", { bytes: blob.size });
    } catch (error) {
      setState("failed");
      track("certificate:exported", { success: false, header_found: body !== markdown });
      log.error("Certificate export failed", { error: getErrorMessage(error) });
    }
  };

  const label =
    state === "exporting"
      ? "Exporting..."
      : state === "failed"
        ? "Export failed"
        : "Export to Word";

  const buttonStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    backgroundColor: hovered && state === "idle" ? "#f5f5f5" : "transparent",
    border: `1px solid ${hovered && state === "idle" ? "#ccc" : "#e0e0e0"}`,
    borderRadius: 6,
    cursor: state === "exporting" ? "wait" : "pointer",
    fontSize: 12,
    fontFamily: "'Segoe UI', sans-serif",
    color: state === "failed" ? "#b91c1c" : "#666",
    transition: "all 0.2s ease",
    outline: "none",
  };

  return (
    <button
      type="button"
      style={buttonStyle}
      onClick={handleExport}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      disabled={state === "exporting"}
      title="Download the certificate as a Word document"
      aria-label="Export to Word"
    >
      {/* The build strips the viewBox from SVG assets, and without one a size
          smaller than the drawing's 24x23 crops it instead of scaling it. */}
      <WordIcon viewBox="0 0 24 23" width="16" height="15" aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
};

export default ExportToWordButton;
